import {
  BreakpointQueryName, FontWeight, SpacingName, ThemedFunctor, TypographyName, getSpacing, getTypography,
} from '@doveit/lego';
import { css } from 'styled-components';
import { WrapperSize } from '../../types';

export const text: ThemedFunctor = ({ theme }) => css`
  ${theme.breakpoints.up(BreakpointQueryName.LG)} {
    text-align: center;
  }
`;

// INTRO SECTION

export const introBlockVideoWrapper: ThemedFunctor = ({ theme }) => css`
  ${getSpacing.margin(theme, [SpacingName.BIGGER, SpacingName.AUTO, 0])}
  max-width: 52rem;
`;

export const introSectionVideo: ThemedFunctor = ({
  theme,
}) => css`
  ${getSpacing.marginTop(theme, SpacingName.MEDIUM)}
  overflow: hidden;
  padding-top: 56.25%;
  position: relative;

  ${theme.breakpoints.up(BreakpointQueryName.LG)} {
    margin: 0;
  }
`;

// COMMON

export const divider: ThemedFunctor = ({
  theme,
}) => css`
  ${getSpacing.padding(theme, [0, SpacingName.SMALL])}
  margin: 0 auto;
  max-width: ${WrapperSize.L};

  &::before {
    content: '';
    height: 1px;
    width: calc(100% + ${theme.spaceUnits.desktop.MEDIUM} + ${theme.spaceUnits.desktop.MEDIUM});
    background-color: ${theme.palettes.neutrals.light};
    display: block;
    transform: translateX(${theme.spaceUnits.desktop['-MEDIUM']});
  }
`;

// ADVANTAGES SECTION

export const advantagesTitle: ThemedFunctor = ({
  theme,
}) => css`
  ${getTypography(theme, TypographyName.H2)}
  ${getSpacing.margin(theme, [SpacingName.MEDIUM, SpacingName.AUTO, 0])}
  ${getSpacing.padding(theme, [0, SpacingName.SMALL])}
  max-width: ${WrapperSize.L};

  ${text({ theme })}
`;

export const advantagesSubtitle: ThemedFunctor = ({
  theme,
}) => css`
  ${text({ theme })}
`;

export const supportImageWrapper: ThemedFunctor = ({
  theme,
}) => css`
  ${getSpacing.marginTop(theme, SpacingName.MEDIUM)}
  max-width: 27rem;
`;

// VALUES SECTION

export const valueBlock: ThemedFunctor = ({ theme }) => css`
  ${getSpacing.padding(theme, [SpacingName.BIG, SpacingName.MEDIUM, SpacingName.MEDIUM])}
  position: relative;
`;

export const valueBlockLogo: ThemedFunctor = ({ theme }) => css`
  ${getSpacing.margin(theme, [SpacingName.SMALL, SpacingName.MEDIUM, 0, 0])}
  position: absolute;
  width: 4.5rem;
  color: ${theme.palettes.brand.primary};
  top: 0;
  right: 0;
`;

// TECHNOLOGY SECTION

export const technologyImageTitle: ThemedFunctor = ({ theme }) => css`
  ${getTypography(theme, TypographyName.H2, { fontWeight: FontWeight.LIGHT })}
  ${getSpacing.marginBottom(theme, SpacingName.MEDIUM)}
  position: relative;
  color: ${theme.palettes.brand.primary};

  ${text({ theme })}
`;

export const valueBlockTitle: ThemedFunctor = ({ theme }) => css`
  ${getTypography(theme, TypographyName.H3)}
  color: ${theme.palettes.brand.primary};
`;

export const valueBlockDescription: ThemedFunctor = ({ theme }) => css`
  ${getSpacing.marginTop(theme, SpacingName.SMALL)}
`;

export const carouselFrame: ThemedFunctor = ({ theme }) => css`
  position: relative;
  border-radius: 1.25rem;
  box-shadow: 0 0 1.25rem rgba(0, 0, 0, 0.1);
  background-color: ${theme.palettes.neutrals.lightest};
  border: 1px solid transparent;

  &::before,
  &::after {
    position: absolute;
    font-size: 7rem;
    color: ${theme.palettes.brand.primary};
    display: flex;
    width: 4rem;
    height: 4rem;
    z-index: ${theme.zIndex.inpage};
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  &::before {
      content: '“';
      top: 0;
      left: 0;
      transform: translateX(-40%) translateY(5%);
    }

    &::after {
      content: '”';
      bottom: 0;
      right: 0;
      transform: translateX(35%) translateY(105%);
    }

  ${theme.breakpoints.up(BreakpointQueryName.LG)} {
    &::before,
    &::after {
      font-size: 10rem;
    }

    &::before {
      transform: translateX(-50%) translateY(25%);
    }

    &::after {
      transform: translateX(50%) translateY(125%);
    }
  }
`;

export const carouselInnerFrame: ThemedFunctor = ({ theme }) => css`
  ${getSpacing.margin(theme, SpacingName.MEDIUM)}
  overflow: hidden;
`;

export const reputationBlock: ThemedFunctor = ({
  theme,
  $desktopCover,
  $mobileCover,
}) => css`
  ${getSpacing.padding(theme, [SpacingName.BIGGER, SpacingName.SMALL])}
  margin: 0 ${theme.spaceUnits.mobile['-SMALL']};
  position: relative;
  background: url('${$mobileCover}') no-repeat;
  background-size: cover;
  background-position: center center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.25));
  }

  ${theme.breakpoints.up(BreakpointQueryName.MD)} {
    margin: 0 ${theme.spaceUnits.desktop['-SMALL']};
  }

  ${theme.breakpoints.up(BreakpointQueryName.LG)} {
    margin: 0;
    background-image: url('${$desktopCover}');
    min-height: 42rem;
    text-align: center;
  }
`;

export const reputationBlockWrapper: ThemedFunctor = ({ theme }) => css`
  position: relative;
  z-index: 1;
  margin: 0 auto;
  max-width: 48rem;
  display: flex;
  flex-direction: column;
  gap: ${theme.spaceUnits.mobile.SMALL};

  ${theme.breakpoints.up(BreakpointQueryName.LG)} {
    gap: ${theme.spaceUnits.desktop.SMALL};
  }
`;
